import { useCallback, useEffect } from 'react';

import { useSearchParams } from 'react-router-dom';

import { useMissedWelcomeBonus } from '../../hooks/useMissedWelcomeBonus';
import { analytics } from '../../services/analytics';
import { auth, type AuthParams } from '../../services/auth';
import { registerAuthComponents } from '../../services/authRegistery';
import { useFlag } from '../../services/launchDarkly';
import { LandingKey } from '../../stores/auth/types';
import { useStores } from '../../stores/useStores';

import { useOpenAuthWizard } from './useOpenAuthWizard';

export function useAuthFlow() {
  const { authStore, userStore, portalStore } = useStores();
  const { setMissedWelcomeBonusUserData } = useMissedWelcomeBonus();
  const isSignUpCountrySelectionEnabled = useFlag<boolean>('sign_up_flow_country_selection', false);
  const isSignUpUiRedesignEnabled = useFlag<boolean>('ui_redesign_registration_flow', true);
  const bingoEnabled = useFlag('bingo_enabled', false);

  const [_, setSearchParams] = useSearchParams();

  const { authorized: isAuthorized, authStatus, isAuthenticationFlowDone, isAuthWizardOpen } = authStore;
  const userFirebaseId = userStore.profile?.firebaseUid;
  const landingKey = authStore.getLandingKey();
  const isDirectLanding = authStore.isDirectLanding();
  const isDirectRegLanding = authStore.isDirectRegLanding();
  const isFootballPromoLanding = landingKey === LandingKey.FOOTBALL_PROMO;
  const isBingoLanding = landingKey === LandingKey.BINGO && bingoEnabled;

  const { openAuthWizard } = useOpenAuthWizard();

  const isThirdPartyFirstLogin = authStatus === 'thirdPartyFirstLogin';
  const isAuthErrorStatus = authStatus === 'authError' || authStatus === 'error';
  const isUserSelfExcludedOrInactive = authStatus === 'userInactive' || authStatus === 'userSelfExcluded';

  const openPostSignUpWizard = useCallback(() => {
    openAuthWizard('postSignUpWizard', {
      force: true,
      persistent: true,
      landingKey: landingKey || undefined,
    });
  }, [landingKey, openAuthWizard]);

  const beforeWelcomeBonusClose = useCallback(() => {
    auth.isAgeConfirmed = false;
    if (isSignUpCountrySelectionEnabled) {
      setTimeout(() => {
        openAuthWizard('ChooseCountry', {
          persistent: true,
          beforeClose: () => {
            authStore.setAuthCompleted();
          },
        });
      }, 200);
    } else {
      authStore.setAuthCompleted();
    }
  }, [authStore, isSignUpCountrySelectionEnabled, openAuthWizard]);

  const sendSignUpEvent = useCallback(
    (params: AuthParams | null) => {
      void analytics.sendEvent('sign_up', {
        step_name: 'sign_up',
        event_action: 'validation',
        is_succeeded: 0,
        sign_up_platform: params?.provider || 'unknown',
        user_id: userStore.profile?.userId.toString() || '',
        landingKey,
      });
    },
    [landingKey, userStore.profile?.userId],
  );

  const handleAuthError = useCallback(() => {
    if (!auth.isAuthError) {
      auth.isAuthError = true;
    }

    openAuthWizard('somethingWentWrongDialog', {
      force: true,
      persistent: isDirectLanding,
      beforeClose: () => {
        auth.isAuthError = false;
        portalStore.setRefreshing(false);
      },
    });
  }, [openAuthWizard, portalStore, isDirectLanding]);

  const openSignUp = useCallback(
    (triggeredBy: string) => {
      if (isDirectRegLanding) {
        openAuthWizard('directRegWizard', {
          triggeredBy,
          force: true,
          persistent: true,
          landingKey: landingKey || undefined,
        });
      } else {
        openAuthWizard('signUpWizard', {
          triggeredBy,
          force: true,
          silent: true,
          persistent: isDirectLanding,
          landingKey: landingKey || undefined,
        });
      }
    },
    [openAuthWizard, isDirectLanding, isDirectRegLanding, landingKey],
  );

  const handleUserInactiveOrSelfExcluded = useCallback(() => {
    openAuthWizard('accountLockedDialog', {
      force: true,
      persistent: isDirectLanding,
      beforeClose: () => {
        auth.isAuthError = false;
        portalStore.setRefreshing(false);
        if (isDirectLanding) {
          openSignUp('account_locked');
        }
      },
    });
  }, [openAuthWizard, portalStore, isDirectLanding, openSignUp]);

  const handleThirdPartFirstLogin = useCallback(() => {
    if (isSignUpUiRedesignEnabled && !isDirectLanding && !isDirectRegLanding) {
      if (userFirebaseId) {
        setMissedWelcomeBonusUserData({ userId: userFirebaseId });
      }
      openPostSignUpWizard();
    } else {
      openAuthWizard('WelcomeBonusDialog', {
        persistent: true,
        beforeClose: beforeWelcomeBonusClose,
      });
    }
  }, [
    openPostSignUpWizard,
    userFirebaseId,
    setMissedWelcomeBonusUserData,
    beforeWelcomeBonusClose,
    isDirectLanding,
    isDirectRegLanding,
    openAuthWizard,
    isSignUpUiRedesignEnabled,
  ]);

  const handleLandingParameterEffects = useCallback(() => {
    if (userStore.loading || !isAuthenticationFlowDone || isAuthErrorStatus) return;

    if (isThirdPartyFirstLogin) {
      handleThirdPartFirstLogin();
      return;
    }

    const isCustomLanding = isDirectLanding || isFootballPromoLanding || isBingoLanding || authStore.isSocialLanding();

    if ((landingKey && !isCustomLanding) || (isCustomLanding && isAuthorized)) {
      authStore.clearLandingKey(setSearchParams);
      return;
    }

    if (authStore.isSocialLanding()) {
      return; // Social landing has its own login UI
    }

    if (isDirectLanding && !isAuthorized && !isAuthWizardOpen) {
      openSignUp('landing_param');
    }
  }, [
    authStore,
    isAuthErrorStatus,
    userStore.loading,
    isAuthorized,
    isAuthenticationFlowDone,
    isThirdPartyFirstLogin,
    handleThirdPartFirstLogin,
    isAuthWizardOpen,
    isDirectLanding,
    isBingoLanding,
    isFootballPromoLanding,
    landingKey,
    openSignUp,
    setSearchParams,
  ]);

  useEffect(() => {
    registerAuthComponents();
  }, []);

  useEffect(() => {
    if (!isAuthErrorStatus) return;

    const params = auth.getAuthParams();
    sendSignUpEvent(params);

    handleAuthError();

    auth.removeAuthParams();
  }, [handleAuthError, sendSignUpEvent, isAuthErrorStatus]);

  useEffect(() => {
    if (isUserSelfExcludedOrInactive) {
      handleUserInactiveOrSelfExcluded();
    }
  }, [isUserSelfExcludedOrInactive, handleUserInactiveOrSelfExcluded]);

  useEffect(() => {
    handleLandingParameterEffects();
  }, [handleLandingParameterEffects]);
}
